import { useEffect, useMemo, useRef, useState } from "react";
import { rateOfRise, temperature, type Interval, type Sample, type TemperatureUnit } from "@anthemreactv2/roast-domain";
import { formatTime, type Workspace } from "./workspace";

const LEFT = 52;
const RIGHT = 20;
const GAP_MS = 3000;
export function isolatedSamples(samples: readonly Sample[], channel: "beanC" | "environmentC"): Sample[] {
  return samples.filter((sample, i) => sample[channel] !== null &&
    (!samples[i - 1] || samples[i - 1]![channel] === null || sample.elapsedMs - samples[i - 1]!.elapsedMs > GAP_MS) &&
    (!samples[i + 1] || samples[i + 1]![channel] === null || samples[i + 1]!.elapsedMs - sample.elapsedMs > GAP_MS));
}
export function plotPath(points: readonly {elapsedMs: number; value: number | null}[], x: (time: number) => number, y: (value: number) => number, maxGapMs: number) {
  let drawing = false;
  let previous = -Infinity;
  const stride = Math.max(1, Math.ceil(points.length / 1200));
  let result = "";
  points.forEach((point, i) => {
    if (point.value === null) { drawing = false; previous = point.elapsedMs; return; }
    if (point.elapsedMs - previous > maxGapMs) drawing = false;
    if (!drawing || i % stride === 0 || i === points.length - 1) {
      result += `${drawing ? "L" : "M"}${x(point.elapsedMs).toFixed(2)},${y(point.value).toFixed(2)} `;
      drawing = true;
    }
    previous = point.elapsedMs;
  });
  return result;
}

export function CurveChart({workspace, unit, interval, windowSeconds, showRor = true}: {
  workspace: Workspace; unit: TemperatureUnit; interval: Interval; windowSeconds: Interval; showRor?: boolean;
}) {
  const chartContainer = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(960);
  useEffect(() => {
    const element = chartContainer.current;
    if (!element) return;
    // Match SVG coordinates to rendered pixels so labels stay legible on small screens.
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(540, Math.round(entry.contentRect.width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const {source, reference, playheadMs, events} = workspace;
  const lastMs = source.samples[source.samples.length - 1]!.elapsedMs;
  const x = (time: number) => LEFT + time / Math.max(lastMs, 1) * (width - LEFT - RIGHT);
  const allTemperatures = useMemo(() => [...source.samples, ...(reference?.samples ?? [])].flatMap(sample =>
    [sample.beanC, sample.environmentC].filter((v): v is number => v !== null).map(v => temperature(v, unit))), [source, reference, unit]);
  const [min, max] = useMemo(() => {
    let low = Infinity; let high = -Infinity;
    for (const value of allTemperatures) { low = Math.min(low, value); high = Math.max(high, value); }
    return [Math.floor(low / 25) * 25 - 25, Math.ceil(high / 25) * 25 + 25];
  }, [allTemperatures]);
  const y = (value: number) => 198 - (value - min) / (max - min) * 176;
  const visible = source.samples.filter(sample => sample.elapsedMs <= playheadMs);
  const markers = (samples: readonly Sample[], channel: "beanC" | "environmentC", className: string) => isolatedSamples(samples, channel).map(sample =>
    <circle key={sample.elapsedMs} className={className} data-reading={channel} cx={x(sample.elapsedMs)} cy={y(temperature(sample[channel]!, unit))} r={2.5} fill="none"><title>{formatTime(sample.elapsedMs)} · {temperature(sample[channel]!, unit).toFixed(1)} °{unit}</title></circle>);
  const temperaturePath = (samples: readonly Sample[], channel: "beanC" | "environmentC") => plotPath(samples.map(sample => ({
    elapsedMs: sample.elapsedMs, value: sample[channel] === null ? null : temperature(sample[channel]!, unit),
  })), x, y, GAP_MS);
  const rorPoints = useMemo(() => {
    const stride = Math.max(1, Math.ceil(source.samples.length / 600));
    // Preserve every discontinuity even when the drawing series is decimated.
    return source.samples.filter((sample, i) => i % stride === 0 || i === source.samples.length - 1 ||
      sample.beanC === null || (i > 0 && sample.elapsedMs - source.samples[i - 1]!.elapsedMs > GAP_MS)).map(sample => ({
      elapsedMs: sample.elapsedMs, value: rateOfRise(source.samples, sample.elapsedMs, {unit, intervalSeconds: interval, windowSeconds, maxGapMs: GAP_MS}),
    }));
  }, [source, unit, interval, windowSeconds]);
  const rorExtent = Math.max(5, ...rorPoints.map(point => Math.abs(point.value ?? 0)));
  const rorY = (value: number) => 55 - value / rorExtent * 37;
  return <figure className="curve-figure">
    <div className="chart-legend"><span className="legend-bean">Bean temperature</span>{source.samples.some(sample => sample.environmentC !== null) && <span className="legend-environment">Environment</span>}{reference && <span className="legend-reference">{reference.name} · reference</span>}</div>
    <div ref={chartContainer} className="chart-scroll" role="region" aria-label="Roast charts, scroll horizontally on small screens" tabIndex={0}>
    <svg viewBox={`0 0 ${width} 238`} role="img" aria-label={`Roast temperatures in degrees ${unit}. Replay position ${formatTime(playheadMs)}. Isolated measured readings appear as points.`}>
      <title>Roast temperature and reference curve</title>
      {[0, 1, 2, 3, 4].map(tick => {
        const value = min + (max - min) * tick / 4;
        return <g key={tick}><line className="gridline" x1={LEFT} x2={width - RIGHT} y1={y(value)} y2={y(value)} /><text x={LEFT - 10} y={y(value) + 4} textAnchor="end">{Math.round(value)}°</text></g>;
      })}
      {[0, 1, 2, 3, 4, 5].map(tick => <g key={tick}><line className="vertical-gridline" x1={x(lastMs * tick / 5)} x2={x(lastMs * tick / 5)} y1={20} y2={208} /><text x={x(lastMs * tick / 5)} y={228} textAnchor="middle">{formatTime(lastMs * tick / 5)}</text></g>)}
      {reference && <path className="reference-line" d={temperaturePath(reference.samples.filter(s => s.elapsedMs <= lastMs), "beanC")} />}
      {reference && markers(reference.samples.filter(s => s.elapsedMs <= lastMs), "beanC", "reference-line")}
      <path className="environment-line" d={temperaturePath(visible, "environmentC")} />
      <path className="bean-line" d={temperaturePath(visible, "beanC")} />
      {markers(visible, "environmentC", "environment-line")}
      {markers(visible, "beanC", "bean-line")}
      {events.map((event, index) => <g key={event.id}><title>{event.name} at {formatTime(event.elapsedMs)}</title><line className="event-line" x1={x(event.elapsedMs)} x2={x(event.elapsedMs)} y1={20} y2={208} /><text className="event-number" x={x(event.elapsedMs) + 5} y={18 + index % 2 * 14}>{index + 1}</text></g>)}
      <line className="playhead" x1={x(playheadMs)} x2={x(playheadMs)} y1={20} y2={208} />
    </svg>
    {showRor && <><div className="ror-heading"><strong>Rate of Rise</strong><span>°{unit} / {interval}s · {windowSeconds}s calculation window</span></div>
    <svg className="ror-chart" viewBox={`0 0 ${width} 110`} role="img" aria-label={`Rate of Rise in degrees ${unit} per ${interval} seconds. Gaps and insufficient history have no value.`}>
      <title>Rate of Rise</title>
      {[-1, 0, 1].map(tick => <g key={tick}><line className="gridline" x1={LEFT} x2={width - RIGHT} y1={rorY(tick * rorExtent)} y2={rorY(tick * rorExtent)} /><text x={LEFT - 10} y={rorY(tick * rorExtent) + 4} textAnchor="end">{(tick * rorExtent).toFixed(0)}</text></g>)}
      <path className="ror-line" d={plotPath(rorPoints.filter(point => point.elapsedMs <= playheadMs), x, rorY, Infinity)} />
    </svg></>}
    </div>
    <figcaption>Missing readings break the curve. Isolated readings appear as points.{showRor && " RoR needs continuous readings across the calculation window."}</figcaption>
  </figure>;
}
